// Return-to-page navigation helpers (H1).
//
// withFrom(): append the caller's current location (path + search) to a detail
// link as ?from=, so the detail page's BackLink can route back to the exact
// filtered/searched list URL. resolveBackTarget(): read + validate that param.
// Only same-origin relative paths are honoured (no //host) to avoid an
// open-redirect via a crafted ?from=.

/** Default "Back to X" label per list route prefix. */
export const RESOURCE_BACK_LABELS: Record<string, string> = {
  "/samples": "Back to samples",
  "/library/loans": "Back to loans",
  "/library": "Back to library",
  "/meetings": "Back to meetings",
  "/briefs": "Back to briefs",
  "/quotes": "Back to quotes",
  "/qa": "Back to QA",
  "/customers": "Back to customers",
  "/suppliers": "Back to suppliers",
  "/orders": "Back to orders",
  "/pipeline": "Back to pipeline",
  "/presentations": "Back to presentations",
  "/actions": "Back to actions",
  "/enquiries": "Back to enquiries",
  "/todo": "Back to my to-do",
};

function isSafeRelative(path: string | null | undefined): path is string {
  return !!path && path.startsWith("/") && !path.startsWith("//");
}

/** Append ?from=<encoded current location> to a detail link. */
export function withFrom(to: string, fromPathAndSearch: string): string {
  if (!isSafeRelative(fromPathAndSearch)) return to;
  const sep = to.includes("?") ? "&" : "?";
  return `${to}${sep}from=${encodeURIComponent(fromPathAndSearch)}`;
}

/** Resolve the back target from a detail page's location.search. */
export function resolveBackTarget(search: string, fallback: string): string {
  const raw = new URLSearchParams(search).get("from");
  return isSafeRelative(raw) ? raw : fallback;
}

/** Best "Back to X" label for a resolved target (longest list-prefix wins). */
export function backLabelFor(target: string, fallback: string): string {
  if (target.startsWith("/meetings/")) return "Back to meeting";
  const hit = Object.keys(RESOURCE_BACK_LABELS)
    .sort((a, b) => b.length - a.length)
    .find(
      (prefix) =>
        target === prefix ||
        target.startsWith(`${prefix}?`) ||
        target.startsWith(`${prefix}/`),
    );
  return hit ? RESOURCE_BACK_LABELS[hit] : fallback;
}
